import { ISelectOption, ISelectGroupedOption } from 'app/interfaces/ISelectOption';

import { isFairfaxEnvironment, isMooncakeEnvironment, isUsNatEnvironment, isUsSecEnvironment, isBleuEnvironment, isDelosEnvironment } from 'environments/environment.util';
import { regions } from 'app/shared/utils/region.util';
import { IVirtualMachineSize } from 'app/interfaces/IAzureResource';
import { GlobalVariables } from 'app/services/globals';
import { expressCustomSetupTypes } from 'app/model/expressCustomSetup/expressCustomSetup';

export class IntegrationRuntimeSSISConst {
    private static readonly _isDogfood: boolean = GlobalVariables.isDogfood;
    public static get ssisIRDefaultRegion(): string {
        if (isFairfaxEnvironment()) {
            return this._isDogfood ? regions.USGovArizona : regions.USGovVirginia;
        }
        if (isMooncakeEnvironment()) {
            return this._isDogfood ? regions.ChinaNorth2 : regions.ChinaEast2;
        }
        if (isUsNatEnvironment()) {
            return regions.UsNatEast;
        }
        if (isUsSecEnvironment()) {
            return regions.UsSecEast;
        }
        if (isBleuEnvironment()) {
            return regions.BleuFranceCentral;
        }
        if (isDelosEnvironment()) {
            return regions.DelosCloudGermanyCentral;
        }
        return this._isDogfood ? regions.WestUS : regions.EastUS2;
    }
}

export const registerAzureBatchCheckCount = 100;

export const nodeMinLength = 1;
export const nodeMaxLength = 10;
export const nodeDefaultLength = 2;
export const executionsPerNodeMinValue = 1;
export const executionsPerNodeMaxValue = 8;
export const ssisIRAllOption = 'All';

export const airflowNodeMinLength = 0;
export const airflowNodeMaxLength = 10;
export const airflowNodeDefaultLength = 0;

export const ssisIRNodeSizes = {
    StandardA4V2: 'Standard_A4_v2',
    StandardA8V2: 'Standard_A8_v2',
    StandardD1V2: 'Standard_D1_v2',
    StandardD2V2: 'Standard_D2_v2',
    StandardD3V2: 'Standard_D3_v2',
    StandardD4V2: 'Standard_D4_v2',
    StandardD2V3: 'Standard_D2_v3',
    StandardD4V3: 'Standard_D4_v3',
    StandardD8V3: 'Standard_D8_v3',
    StandardD16V3: 'Standard_D16_v3',
    StandardD32V3: 'Standard_D32_v3',
    StandardD64V3: 'Standard_D64_v3',
    StandardE2V3: 'Standard_E2_v3',
    StandardE4V3: 'Standard_E4_v3',
    StandardE8V3: 'Standard_E8_v3',
    StandardE16V3: 'Standard_E16_v3',
    StandardE32V3: 'Standard_E32_v3',
    StandardE64V3: 'Standard_E64_v3',
    StandardE64iV3: 'Standard_E64i_v3',
    StandardE80idsV4: 'Standard_E80ids_v4'
};

// Due to cannot get quota for other region, we currently only open few regions for isolated vm
export function getSsisIRNodeSize(region: string): string[] {
    switch (region) {
        case regions.EastUS:
        case regions.SouthCentralUS:
        case regions.WestUS:
        case regions.EastUS2:
        case regions.WestEurope:
            return ssisIRNodeSizeValueIncludingV3.concat([ssisIRNodeSizes.StandardE64iV3,
            ssisIRNodeSizes.StandardE80idsV4]);
        case regions.NorthEurope:
            return ssisIRNodeSizeValueIncludingV3.concat([ssisIRNodeSizes.StandardE64iV3]);
        default:
            return ssisIRNodeSizeValueIncludingV3;
    }
}

export const ssisIRNodeSizeValueIncludingV3 = [
    ssisIRNodeSizes.StandardD2V3,
    ssisIRNodeSizes.StandardD4V3,
    ssisIRNodeSizes.StandardD8V3,
    ssisIRNodeSizes.StandardD16V3,
    ssisIRNodeSizes.StandardD32V3,
    ssisIRNodeSizes.StandardD64V3,
    ssisIRNodeSizes.StandardE2V3,
    ssisIRNodeSizes.StandardE4V3,
    ssisIRNodeSizes.StandardE8V3,
    ssisIRNodeSizes.StandardE16V3,
    ssisIRNodeSizes.StandardE32V3,
    ssisIRNodeSizes.StandardE64V3,
    ssisIRNodeSizes.StandardD1V2,
    ssisIRNodeSizes.StandardD2V2,
    ssisIRNodeSizes.StandardD3V2,
    ssisIRNodeSizes.StandardD4V2,
    ssisIRNodeSizes.StandardA4V2,
    ssisIRNodeSizes.StandardA8V2
];

export const nodeSizeMaxExecutionMap = new Map([
    ['Standard_A4_v2', 8],
    ['Standard_A8_v2', 16],
    ['Standard_D1_v2', 4],
    ['Standard_D2_v2', 8],
    ['Standard_D3_v2', 8],
    ['Standard_D4_v2', 16],
    ['Standard_D2_v3', 8],
    ['Standard_D4_v3', 8],
    ['Standard_D8_v3', 16],
    ['Standard_D16_v3', 32],
    ['Standard_D32_v3', 64],
    ['Standard_D64_v3', 128],
    ['Standard_E2_v3', 8],
    ['Standard_E4_v3', 8],
    ['Standard_E8_v3', 16],
    ['Standard_E16_v3', 32],
    ['Standard_E32_v3', 64],
    ['Standard_E64_v3', 128],
    ['Standard_E64i_v3', 128],
    ['Standard_E80ids_v4', 128]
]);

export const nodeSizeCoreNumberMap = new Map([
    ['Standard_A4_v2', 4],
    ['Standard_A8_v2', 8],
    ['Standard_D1_v2', 1],
    ['Standard_D2_v2', 2],
    ['Standard_D3_v2', 4],
    ['Standard_D4_v2', 8],
    ['Standard_D2_v3', 2],
    ['Standard_D4_v3', 4],
    ['Standard_D8_v3', 8],
    ['Standard_D16_v3', 16],
    ['Standard_D32_v3', 32],
    ['Standard_D64_v3', 64],
    ['Standard_E2_v3', 2],
    ['Standard_E4_v3', 4],
    ['Standard_E8_v3', 8],
    ['Standard_E16_v3', 16],
    ['Standard_E32_v3', 32],
    ['Standard_E64_v3', 64],
    ['Standard_E64i_v3', 64],
    ['Standard_E80ids_v4', 80]
]);

export const defaultNodeSize = ssisIRNodeSizes.StandardD8V3;

export const nodeSizesConstant: Array<ISelectOption<string>> = [
    {
        label: stringResources.Node_Size_Standard_A4_V2,
        value: ssisIRNodeSizes.StandardA4V2
    }, {
        label: stringResources.Node_Size_Standard_A8_V2,
        value: ssisIRNodeSizes.StandardA8V2
    }, {
        label: stringResources.Node_Size_Standard_D1_V2,
        value: ssisIRNodeSizes.StandardD1V2
    }, {
        label: stringResources.Node_Size_Standard_D2_V2,
        value: ssisIRNodeSizes.StandardD2V2
    }, {
        label: stringResources.Node_Size_Standard_D3_V2,
        value: ssisIRNodeSizes.StandardD3V2
    }, {
        label: stringResources.Node_Size_Standard_D4_V2,
        value: ssisIRNodeSizes.StandardD4V2
    }
];

// Get VM size in dogfood environment has been deprecated. We use const list here
export const dogfoodNodeList: IVirtualMachineSize[] = [
    {
        name: 'Standard_D2_v3',
        numberOfCores: 2,
        memoryInMB: 8192
    }, {
        name: 'Standard_D4_v3',
        numberOfCores: 4,
        memoryInMB: 16384
    }, {
        name: 'Standard_D8_v3',
        numberOfCores: 8,
        memoryInMB: 32768
    }
];

export const ssisIRSkus = {
    Standard: 'Standard',
    Enterprise: 'Enterprise'
};

export const ssisRuntimeVersion = {
    Sql2017: '2017',
    Latest: 'latest'
};

export const runtimeVersionConstant: Array<ISelectOption<string>> = [
    {
        label: stringResources.IRCreation_SSIS_RuntimeVersion_SQL_2017,
        value: ssisRuntimeVersion.Sql2017
    }, {
        label: stringResources.IRCreation_SSIS_RuntimeVersion_Azure_SSIS,
        value: ssisRuntimeVersion.Latest
    }
];

export const skusConstant: Array<ISelectOption<string>> = [
    {
        label: stringResources.Standard,
        value: ssisIRSkus.Standard
    }, {
        label: stringResources.IRCreation_SSIS_Enterprise,
        value: ssisIRSkus.Enterprise
    }
];

export const ssisIRLicenseTypes = {
    LicenseIncluded: 'LicenseIncluded',
    BasePrice: 'BasePrice'
};

export const ssisIRTiers = {
    B: 'Basic',
    S0: 'S0',
    S1: 'S1',
    S2: 'S2',
    S3: 'S3',
    P1: 'P1',
    P2: 'P2',
    P4: 'P4',
    P6: 'P6',
    P11: 'P11',
    P15: 'P15',
    PRS1: 'PRS1',
    PRS2: 'PRS2',
    PRS4: 'PRS4',
    PRS6: 'PRS6'
};

export const ssisDefaultTierValue = ssisIRTiers.S1;

export const tiersConstant: Array<ISelectOption<string>> = [
    {
        label: stringResources.Basic_DisplayText,
        value: ssisIRTiers.B
    }, {
        label: stringResources.Tier_S0,
        value: ssisIRTiers.S0
    }, {
        label: stringResources.Tier_S1,
        value: ssisIRTiers.S1
    }, {
        label: stringResources.Tier_S2,
        value: ssisIRTiers.S2
    }, {
        label: stringResources.Tier_S3,
        value: ssisIRTiers.S3
    }, {
        label: stringResources.Tier_P1,
        value: ssisIRTiers.P1
    }, {
        label: stringResources.Tier_P2,
        value: ssisIRTiers.P2
    }, {
        label: stringResources.Tier_P4,
        value: ssisIRTiers.P4
    }, {
        label: stringResources.Tier_P6,
        value: ssisIRTiers.P6
    }, {
        label: stringResources.Tier_P11,
        value: ssisIRTiers.P11
    }, {
        label: stringResources.Tier_P15,
        value: ssisIRTiers.P15
    }, {
        label: stringResources.Tier_PRS1,
        value: ssisIRTiers.PRS1
    }, {
        label: stringResources.Tier_PRS2,
        value: ssisIRTiers.PRS2
    }, {
        label: stringResources.Tier_PRS4,
        value: ssisIRTiers.PRS4
    }, {
        label: stringResources.Tier_PRS6,
        value: ssisIRTiers.PRS6
    }
];

export const vnetTypes = {
    Classic: 'Microsoft.ClassicNetwork/virtualNetworks',
    Arm: 'Microsoft.Network/virtualNetworks'
};

export const vnetTypeOptionsConstant: Array<ISelectOption<string>> = [
    {
        label: stringResources.ArmVirtualNetwork,
        value: vnetTypes.Arm
    }, {
        label: stringResources.ClassicVirtualNetWork,
        value: vnetTypes.Classic
    }
];

export const armVnetTypeOptionsConstant: Array<ISelectOption<string>> = [
    {
        label: stringResources.ArmVirtualNetwork,
        value: vnetTypes.Arm
    }
];

export const groupedStandaloneTierConstant: Array<ISelectGroupedOption<string>> = [
    {
        label: stringResources.Tier_Group_Name_Single_Database,
        items: tiersConstant
    }
];

export const subnetsKeyInResourceId: string = '/subnets/';

export function getCustomSetupTypeOptions(): Array<ISelectOption<string>> {
    const options: Array<ISelectOption<string>> = [
        {
            label: stringResources.Select,
            value: undefined
        },
        {
            label: stringResources.IRCreation_SSIS_CustomSetup_CmdKey,
            value: expressCustomSetupTypes.Cmdkey
        },
        {
            label: stringResources.IRCreation_SSIS_CustomSetup_EnvrionmentVariable,
            value: expressCustomSetupTypes.EnvVariable
        }
    ];
    options.push({
        label: stringResources.IRCreation_SSIS_CustomSetup_Install,
        value: expressCustomSetupTypes.Installation
    });
    options.push({
        label: stringResources.IRCreation_SSIS_CustomSetup_AzPowerShell,
        value: expressCustomSetupTypes.AzPowerShell
    });
    return options;
}

